<template>
  <div class="reports-table">
    <div class="reports-table__filter-bar">
      <b-form-select v-model="timeRange" :options="CONTENT_REPORT_TIME_RANGE_OPTIONS" class="reports-table__filter" />
      <b-form-select
        v-model="statusFilter"
        :options="CONTENT_REPORT_STATUS_FILTER_BAR_OPTIONS"
        class="reports-table__filter"
        @change="statusFilterTouched = true"
      />
      <b-form-select v-model="countFilter" :options="CONTENT_REPORT_COUNT_OPTIONS" class="reports-table__filter">
        <template #first>
          <b-form-select-option :value="null" disabled>Report number</b-form-select-option>
        </template>
      </b-form-select>
      <b-button variant="link" class="reports-table__settings-btn" title="Report settings" @click="onOpenSettings">
        <font-awesome-icon :icon="['fal', 'cog']" />
      </b-button>
    </div>

    <app-loading :loading="loading" />
    <template v-if="!loading">
      <b-alert :show="showTruncationBanner" variant="warning" class="reports-table__truncation py-2">
        <template v-if="hasMissingPending">
          {{ pendingTotal }} reports are pending, but {{ missingPendingCount }} can't be shown here — the list is capped
          at the most recent {{ reports.length }} items. Resolve or dismiss reports to surface the rest.
        </template>
        <template v-else>
          Showing {{ filteredReports.length }} of the most recent {{ reports.length }} reported items. Resolve or
          dismiss reports to surface older ones.
        </template>
      </b-alert>
      <b-table
        v-if="filteredReports.length > 0"
        :items="filteredReports"
        :fields="TABLE_FIELDS"
        hover
        responsive
        borderless
        show-empty
        class="reports-table__table"
      >
        <template #cell(entity_type)="{ item }">
          <b-badge pill variant="light" class="reports-table__type-badge">
            {{ entityLabel(item.entity_type) }}
          </b-badge>
        </template>

        <template #cell(content_preview)="{ item }">
          <span class="reports-table__preview" :class="{ 'text-muted': !item.content_preview }">
            {{ item.content_preview || '(no text content)' }}
          </span>
        </template>

        <template #cell(author_name)="{ item }">
          <span>{{ item.author_name || '-' }}</span>
        </template>

        <template #cell(last_reported_at)="{ item }">
          <span>{{ formatDate(item.last_reported_at) }}</span>
        </template>

        <template #cell(status)="{ item }">
          <b-badge pill :variant="statusVariant(item)">{{ statusLabel(item) }}</b-badge>
        </template>

        <template #cell(actions)="{ item }">
          <b-button size="xs" variant="outline-primary" @click="emit('review', item)">Review</b-button>
        </template>
      </b-table>

      <div v-else class="reports-table__empty">
        <p class="text-muted">No reported content to review.</p>
      </div>
    </template>

    <!-- lazy: remount ReportSettings on each open so it re-reads the latest
         persisted settings from company state (see ReportSettings.vue). -->
    <b-modal ref="settingsModalRef" title="Reported content settings" size="lg" hide-footer centered lazy>
      <report-settings @saved="settingsModalRef?.hide()" />
    </b-modal>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import AppLoading from '@/components/AppLoading.vue';
import ReportSettings from '@/pages/recognition/components/ReportSettings.vue';
import {
  CONTENT_REPORT_ENTITY_LABELS,
  CONTENT_REPORT_STATUSES,
  CONTENT_REPORT_STATUS_FILTERS,
  CONTENT_REPORT_STATUS_FILTER_BAR_OPTIONS,
  CONTENT_REPORT_TIME_RANGES,
  CONTENT_REPORT_TIME_RANGE_OPTIONS,
  CONTENT_REPORT_COUNT_FILTERS,
  CONTENT_REPORT_COUNT_OPTIONS,
  getContentReportTimeRangeFrom,
} from '@/pages/recognition/constants';

const props = defineProps({
  reports: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  truncated: {
    type: Boolean,
    default: false,
  },
  // True server-side pending total (the danger badge count). Used to reconcile
  // against the pending items actually present in the fetched (capped) page, so
  // an admin is never told "N pending" while some pending items are unreachable.
  pendingTotal: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['review']);

const timeRange = ref(CONTENT_REPORT_TIME_RANGES.ALL);
const statusFilter = ref(CONTENT_REPORT_STATUS_FILTERS.ALL);
// null = the disabled "Report number" placeholder shown as the default in the box;
// it filters the same as ALL (matchesCount falls through to "show all").
const countFilter = ref(null);

// Default the status filter to Pending when reports are awaiting review, else All.
// `statusFilterTouched` is set by the select's @change (user action only — a
// programmatic v-model update here does not emit change), so once the admin picks
// a status we stop overriding their choice as the pending count changes.
const statusFilterTouched = ref(false);
watch(
  () => props.pendingTotal,
  total => {
    if (!statusFilterTouched.value) {
      statusFilter.value = total > 0 ? CONTENT_REPORT_STATUS_FILTERS.PENDING : CONTENT_REPORT_STATUS_FILTERS.ALL;
    }
  },
  { immediate: true },
);

const settingsModalRef = ref(null);
const onOpenSettings = () => settingsModalRef.value?.show();

const matchesTime = item => {
  const from = getContentReportTimeRangeFrom(timeRange.value);
  if (!from) {
    return true;
  }
  if (!item.last_reported_at) {
    return false;
  }
  return new Date(item.last_reported_at) >= from;
};

const matchesStatus = item => {
  switch (statusFilter.value) {
    case CONTENT_REPORT_STATUS_FILTERS.PENDING:
      return item.status === CONTENT_REPORT_STATUSES.PENDING;
    case CONTENT_REPORT_STATUS_FILTERS.HIDDEN:
      return item.is_hidden === true;
    case CONTENT_REPORT_STATUS_FILTERS.RESOLVED:
      // Admin reviewed and took action — includes reinstated (visible) items,
      // which would otherwise only appear under "All".
      return item.status === CONTENT_REPORT_STATUSES.ACTION_TAKEN;
    case CONTENT_REPORT_STATUS_FILTERS.DISMISSED:
      return item.status === CONTENT_REPORT_STATUSES.DISMISSED;
    default:
      return true;
  }
};

const matchesCount = item => {
  switch (countFilter.value) {
    case CONTENT_REPORT_COUNT_FILTERS.ONE:
      return item.report_count === 1;
    case CONTENT_REPORT_COUNT_FILTERS.TWO:
      return item.report_count === 2;
    case CONTENT_REPORT_COUNT_FILTERS.THREE_PLUS:
      return item.report_count >= 3;
    default:
      return true;
  }
};

const filteredReports = computed(() =>
  props.reports.filter(item => matchesTime(item) && matchesStatus(item) && matchesCount(item)),
);

// Pending items present in the fetched (capped) page.
const pendingShownCount = computed(
  () => props.reports.filter(item => item.status === CONTENT_REPORT_STATUSES.PENDING).length,
);

// How many pending reports the server counts but that aren't in this page —
// they've been pushed past the per-page cap and are currently unreachable.
const missingPendingCount = computed(() => Math.max(0, props.pendingTotal - pendingShownCount.value));

// Moderation-critical: never let the danger badge claim N pending while some
// pending items are silently unreachable. Surface this whenever it happens,
// regardless of the active filter (the banner text below is filter-aware).
const hasMissingPending = computed(() => missingPendingCount.value > 0);

// Show the banner when pending items are unreachable, or when the page itself is
// full (older non-pending items may be hidden). The message references the
// displayed (filtered) context rather than the raw fetched count.
const showTruncationBanner = computed(() => hasMissingPending.value || props.truncated);

const TABLE_FIELDS = [
  { key: 'entity_type', label: 'Type' },
  { key: 'content_preview', label: 'Content' },
  { key: 'author_name', label: 'Author' },
  { key: 'report_count', label: 'Reports' },
  { key: 'last_reported_at', label: 'Last reported' },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: '', class: 'text-right' },
];

const entityLabel = entityType => CONTENT_REPORT_ENTITY_LABELS[entityType] || entityType;

const formatDate = dateStr => {
  if (!dateStr) {
    return '-';
  }
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Hidden + still PENDING = auto-hidden on report, awaiting admin review.
// Hidden + ACTION_TAKEN = an admin reviewed it and chose to keep it hidden.
const statusLabel = item => {
  if (item.is_hidden && item.status === CONTENT_REPORT_STATUSES.PENDING) {
    return 'Auto hidden';
  }
  if (item.is_hidden) {
    return 'Hidden';
  }
  if (item.status === CONTENT_REPORT_STATUSES.PENDING) {
    return 'Pending';
  }
  if (item.status === CONTENT_REPORT_STATUSES.ACTION_TAKEN) {
    return 'Action taken';
  }
  return 'Dismissed';
};

const statusVariant = item => {
  // Auto-hidden items are awaiting review, so use the pending colour.
  if (item.is_hidden && item.status === CONTENT_REPORT_STATUSES.PENDING) {
    return 'warning';
  }
  if (item.is_hidden) {
    return 'danger';
  }
  if (item.status === CONTENT_REPORT_STATUSES.PENDING) {
    return 'warning';
  }
  return 'secondary';
};
</script>

<style lang="scss" scoped>
.reports-table {
  &__filter-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1rem;
    margin-bottom: 1rem;
    background: var(--palette-color-default-lighten-95);
    border-radius: 0.5rem;
  }

  &__filter {
    width: auto;
    min-width: 150px;
    border-color: var(--palette-color-default-lighten-80);
    padding: 0.5rem 1rem;

    &:focus {
      border-color: var(--palette-color-primary);
      box-shadow: none;
    }
  }

  &__settings-btn {
    margin-left: auto;
    color: var(--palette-color-default);
    text-decoration: none;
    padding: 0.5rem 0.75rem;
    font-size: 1.125rem;
    background-color: transparent;
    border-color: transparent;

    &:hover {
      color: var(--palette-color-default-lighten-20);
      text-decoration: none;
      background-color: transparent;
      border-color: transparent;
    }

    // No focus/click box, background, outline or box-shadow — only the hover
    // colour change. !important overrides Bootstrap's .btn:active/.focus states
    // that would otherwise paint a grey box behind the icon when clicked.
    &:focus,
    &:active,
    &:focus-visible,
    &.active {
      box-shadow: none !important;
      outline: none;
      text-decoration: none;
      background-color: transparent !important;
      border-color: transparent !important;
    }
  }

  &__table {
    :deep(thead th) {
      font-weight: 500;
      font-size: 0.875rem;
      color: var(--palette-color-default-lighten-40);
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
      padding: 0.75rem 1rem;
    }

    :deep(tbody td) {
      padding: 1rem;
      vertical-align: middle;
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
    }

    :deep(tbody tr:hover) {
      background-color: var(--palette-color-default-lighten-95);
    }
  }

  &__preview {
    display: inline-block;
    max-width: 24rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
  }

  &__type-badge {
    background-color: var(--palette-color-default-lighten-90);
    color: var(--palette-color-default);
    font-weight: 400;
    padding: 0.375rem 0.75rem;
  }

  &__empty {
    padding: 3rem;
    text-align: center;
  }
}
</style>
